import { useState } from "react";
import { toast } from "sonner";
import { Clock, CalendarPlus } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { Input } from "../ui/input";
import { plannedActivitiesApi } from "../../lib/plannedActivitiesApi";

/**
 * Quick-postpone button — surfaces on overdue activity rows. Opens a
 * compact popover with common reschedule intervals plus a custom date
 * picker. Preserves the original time-of-day when possible so a "9:00
 * Ericsson call" pushed by 1 day stays at 9:00 the next morning.
 */
const QUICK_OFFSETS = [
  { key: 1,  label: "+1 gün" },
  { key: 3,  label: "+3 gün" },
  { key: 7,  label: "+1 hafta" },
];

export default function PostponeButton({ activity, onDone }) {
  const [saving, setSaving] = useState(false);
  const [customDate, setCustomDate] = useState("");
  const [open, setOpen] = useState(false);

  const currentTime = (() => {
    if (!activity.scheduled_at) return "09:00";
    const d = new Date(activity.scheduled_at);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  })();

  const postpone = async (daysAhead, explicitDate) => {
    setSaving(true);
    try {
      let iso;
      if (explicitDate) {
        // Explicit date from picker — carry over original time-of-day.
        iso = new Date(`${explicitDate}T${currentTime}:00`).toISOString();
      } else {
        // Offset in days from now, preserving the original time.
        const target = new Date();
        target.setDate(target.getDate() + daysAhead);
        const [hh, mm] = currentTime.split(":");
        target.setHours(parseInt(hh, 10), parseInt(mm, 10), 0, 0);
        iso = target.toISOString();
      }
      await plannedActivitiesApi.update(activity.id, { scheduled_at: iso });
      toast.success("Aktivite ötelendi");
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ötelenemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={saving}
          data-testid={`postpone-${activity.id}`}
          title="Aktiviteyi ötele"
          className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800 transition-colors hover:bg-amber-100"
        >
          <Clock className="h-2.5 w-2.5" />
          Ötele
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[240px] p-3"
        align="start"
        data-testid={`postpone-popover-${activity.id}`}
      >
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-zinc-800">
          <CalendarPlus className="h-3.5 w-3.5 text-amber-600" />
          Yeniden Planla
        </div>
        <div className="flex flex-wrap gap-1.5">
          {QUICK_OFFSETS.map((q) => (
            <button
              key={q.key}
              type="button"
              disabled={saving}
              onClick={() => postpone(q.key)}
              data-testid={`postpone-quick-${q.key}`}
              className="rounded-full border border-amber-200 bg-white px-2.5 py-1 text-[11px] font-medium text-amber-800 hover:border-amber-400 hover:bg-amber-50"
            >
              {q.label}
            </button>
          ))}
        </div>
        <div className="mt-3 border-t border-zinc-100 pt-2">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
            Özel tarih
          </div>
          <div className="flex items-center gap-1.5">
            <Input
              type="date"
              value={customDate}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setCustomDate(e.target.value)}
              className="h-8 text-xs"
              data-testid={`postpone-custom-date-${activity.id}`}
            />
            <button
              type="button"
              onClick={() => customDate && postpone(0, customDate)}
              disabled={!customDate || saving}
              data-testid={`postpone-custom-submit-${activity.id}`}
              className="rounded-md bg-amber-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
            >
              Ötele
            </button>
          </div>
          <p className="mt-1 text-[10px] text-zinc-500">
            Orijinal saat ({currentTime}) korunur.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
